import { calcTotals } from '@florezjaramillo/florezjaramillo-core'
import { useSearchParams } from '@solidjs/router'
import { type Component, createEffect, createMemo, createResource, createSignal, For, Show } from 'solid-js'
import { createStore, produce } from 'solid-js/store'
import Button from '~/components/Button'
import Card from '~/components/Card'
import DateInput from '~/components/DateInput'
import Form from '~/components/Form'
import GoBack from '~/components/GoBack'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Menu from '~/components/Menu'
import Select from '~/components/Select'
import Textarea from '~/components/Textarea'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompany } from '~/contexts/CompanyContext'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { useIsMobile } from '~/hooks/useIsMobile'
import DiscountField from '~/layers/ui/admin/shared/DiscountField'
import LineItemModal from '~/layers/ui/admin/shared/LineItemModal'
import LineSwipeCard from '~/layers/ui/admin/shared/LineSwipeCard'
import LineTable from '~/layers/ui/admin/shared/LineTable'
import { type DiscountTypeType, emptyLine, type LineType, lineFromService } from '~/layers/ui/admin/shared/line'
import NewClientModal, { type NewClientValuesType } from '~/layers/ui/admin/shared/NewClientModal'
import NewServiceModal, { type NewServiceValuesType } from '~/layers/ui/admin/shared/NewServiceModal'
import { useNavigate } from '~/router'
import { companyService } from '~/services/company.service'
import { customerService } from '~/services/customer.service'
import { type QuoteType, quoteService } from '~/services/quote.service'
import { serviceCatalogService } from '~/services/serviceCatalog.service'
import { resolveError } from '~/utils/error'
import { currencyOptions, formatMoney, getCurrencyLocale } from '~/utils/settings'
import { quoteStatusLabels as statusLabels } from '~/utils/status'

const STATUS_OPTIONS = Object.entries(statusLabels).map(([value, title]) => ({ value, title }))

type QuoteLineType = LineType

interface QuoteDefaultsType {
  currency?: string
  taxRate?: number
  terms?: string
  validDays?: number
}

const QuoteForm: Component<{ quote: () => QuoteType | null }> = props => {
  const toast = useToast()
  const alert = useAlert()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { currentCompany } = useCompany()
  const { settings } = useCompanySettings()

  const [customers, { refetch: refetchCustomers }] = createResource(async () => {
    try {
      return (await customerService.list({ limit: 200 })).items
    } catch {
      return []
    }
  })
  const [catalog, { refetch: refetchCatalog }] = createResource(async () => {
    try {
      return await serviceCatalogService.list()
    } catch {
      return []
    }
  })
  const [defaults] = createResource(
    () => currentCompany().id,
    async (companyId: string) => {
      try {
        const company = await companyService.get(companyId)
        return ((company.settings ?? {}) as { quoteDefaults?: QuoteDefaultsType }).quoteDefaults ?? {}
      } catch {
        return {} as QuoteDefaultsType
      }
    }
  )
  const [isSubmitting, setIsSubmitting] = createSignal(false)
  const [lines, setLines] = createStore<QuoteLineType[]>([])
  /**
   * `?cliente=ID` deja el cliente ya elegido. Lo usa el tablero del CRM para
   * cotizar desde la tarjeta sin tener que buscarlo otra vez en la lista.
   */
  const [customerId, setCustomerId] = createSignal<string>(searchParams.cliente ? String(searchParams.cliente) : '')
  const [currency, setCurrency] = createSignal('COP')
  const [status, setStatus] = createSignal('draft')
  const [validUntil, setValidUntil] = createSignal('')
  const [taxRate, setTaxRate] = createSignal(0)
  const [globalDiscount, setGlobalDiscount] = createSignal(0)
  const [globalDiscountType, setGlobalDiscountType] = createSignal<DiscountTypeType>('amount')
  const [titleValue, setTitleValue] = createSignal('')
  const [descriptionValue, setDescriptionValue] = createSignal('')
  const [notesValue, setNotesValue] = createSignal('')
  const [termsValue, setTermsValue] = createSignal('')
  const [initialized, setInitialized] = createSignal(false)

  const [showClientModal, setShowClientModal] = createSignal(false)
  const [showLineModal, setShowLineModal] = createSignal(false)
  const [editingLineIndex, setEditingLineIndex] = createSignal<number | null>(null)
  const isMobile = useIsMobile()
  const [isCreatingClient, setIsCreatingClient] = createSignal(false)
  const [showServiceModal, setShowServiceModal] = createSignal(false)
  const [isCreatingService, setIsCreatingService] = createSignal(false)

  const customerOptions = createMemo(() => (customers() ?? []).map(c => ({ value: String(c.id), title: c.name, subtitle: c.document ?? undefined })))

  const selectedCustomer = createMemo(() => (customers() ?? []).find(c => String(c.id) === customerId()) ?? null)

  /** Monedas y locales vienen de `companies.settings` (configurables, no hardcodeados). */
  const monedaOptions = createMemo(() => currencyOptions(settings()?.currencies ?? []))
  const monedaLocale = createMemo(() => getCurrencyLocale(settings()?.currencies ?? [], currency()))

  const catalogOptions = createMemo(() =>
    (catalog() ?? []).filter(s => s.active).map(s => ({ value: String(s.id), title: s.name, subtitle: s.price ? `desde ${s.price.toLocaleString('es-CO')}` : undefined }))
  )

  const totals = createMemo(() => {
    const t = calcTotals(lines, taxRate(), globalDiscount(), globalDiscountType())
    return { subtotal: t.subtotal, discount: t.discountAmount, taxBase: t.taxBase, taxAmount: t.taxAmount, total: t.total }
  })

  const money = (value: number) => formatMoney(value, currency(), settings()?.currencies ?? [])

  const resetForm = () => {
    const preset = defaults() ?? {}
    setLines([emptyLine()])
    // Si se llegó con `?cliente=ID` (desde el tablero del CRM) se respeta:
    // el usuario ya eligió a quién cotizar y no debe volver a buscarlo.
    setCustomerId(searchParams.cliente ? String(searchParams.cliente) : '')
    setCurrency(preset.currency ?? 'COP')
    setStatus('draft')
    setTaxRate(preset.taxRate ?? 0)
    setGlobalDiscount(0)
    setGlobalDiscountType('amount')
    setValidUntil(preset.validDays ? new Date(Date.now() + preset.validDays * 86_400_000).toISOString().slice(0, 10) : '')
    setTitleValue('')
    setDescriptionValue('')
    setNotesValue('')
    setTermsValue(preset.terms ?? '')
  }

  const loadEditData = (quote: QuoteType) => {
    setLines(
      (quote.items ?? [])
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(item => ({
          description: item.description,
          quantity: Number(item.quantity) || 0,
          unitPrice: Number(item.unitPrice) || 0,
          discount: Number(item.discount) || 0,
          discountType: (item.discountType as DiscountTypeType) ?? 'amount',
          notes: item.notes ?? ''
        }))
    )
    setCustomerId(quote.customerId ? String(quote.customerId) : '')
    setCurrency(quote.currency)
    setStatus(quote.status)
    setTaxRate(Number(quote.taxRate) || 0)
    setGlobalDiscount(Number(quote.discount) || 0)
    setGlobalDiscountType((quote.discountType as DiscountTypeType) ?? 'amount')
    setValidUntil(quote.validUntil ? String(quote.validUntil).slice(0, 10) : '')
    setTitleValue(quote.title)
    setDescriptionValue(quote.description ?? '')
    setNotesValue(quote.notes ?? '')
    setTermsValue(quote.terms ?? '')
  }

  /**
   * Al editar hay que esperar a que llegue la cotización. Antes se daba el
   * formulario por inicializado en cuanto había defaults y, si la petición aún
   * no había respondido, se pintaba un formulario vacío: los campos salían en
   * blanco y "Actualizar" no hacía nada (el título obligatorio estaba vacío).
   */
  createEffect(() => {
    if (initialized()) return

    const quote = props.quote()
    if (quote) {
      loadEditData(quote)
    } else {
      if (defaults.loading) return
      resetForm()
    }

    setInitialized(true)
  })

  const openLineModal = (index: number | null = null) => {
    setEditingLineIndex(index)
    setShowLineModal(true)
  }

  /** Guarda la línea: nueva al final o reemplazando la que se está editando. */
  const saveLine = (line: LineType) => {
    const index = editingLineIndex()
    if (index != null) setLines(index, line)
    else setLines(produce(draft => draft.push(line)))
    setEditingLineIndex(null)
  }

  const removeLine = async (index: number) => {
    const ok = await alert.confirmDelete({ title: '¿Quitar línea?', subtitle: 'Se eliminará este servicio de la cotización.' })
    if (ok) setLines(produce(draft => draft.splice(index, 1)))
  }

  const duplicateLine = (index: number) => setLines(produce(draft => draft.splice(index + 1, 0, { ...draft[index] })))

  const handleCreateClient = async (values: NewClientValuesType) => {
    setIsCreatingClient(true)
    try {
      const payload = {
        name: values.name,
        document: values.document || undefined,
        phone: values.phone || undefined,
        address: values.address || undefined
      }
      const created = await customerService.create(payload)
      toast.addToast({ title: 'Cliente creado', type: 'success' })
      await refetchCustomers()
      setCustomerId(String(created.id))
      setShowClientModal(false)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsCreatingClient(false)
    }
  }

  /** Crea el servicio en el catálogo y lo añade de una vez a esta cotización. */
  const handleCreateService = async (values: NewServiceValuesType) => {
    setIsCreatingService(true)
    try {
      const created = await serviceCatalogService.create({
        name: values.name,
        description: values.description,
        price: Number(values.price) || 0,
        unit: values.unit
      })
      toast.addToast({ title: 'Servicio creado y añadido', type: 'success' })
      await refetchCatalog()
      setLines(produce(draft => draft.push(lineFromService(created))))
      setShowServiceModal(false)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsCreatingService(false)
    }
  }

  const handleSubmit = async (formData: FormData) => {
    const items = lines
      .filter(line => line.description.trim())
      .map((line, index) => ({
        description: line.description.trim(),
        quantity: Number(line.quantity) || 0,
        unitPrice: Number(line.unitPrice) || 0,
        discount: Number(line.discount) || 0,
        discountType: line.discountType,
        notes: line.notes.trim() || undefined,
        order: index
      }))

    if (items.length === 0) {
      toast.addToast({ title: 'Añade al menos un servicio a la cotización', type: 'warning' })
      return
    }

    setIsSubmitting(true)
    try {
      const payload = {
        customerId: customerId() ? Number(customerId()) : undefined,
        title: String(formData.get('title') ?? '').trim(),
        description: String(formData.get('description') ?? '').trim() || undefined,
        currency: currency(),
        taxRate: taxRate(),
        discount: globalDiscount(),
        discountType: globalDiscountType(),
        validUntil: validUntil() ? new Date(`${validUntil()}T00:00:00`).toISOString() : undefined,
        notes: String(formData.get('notes') ?? '').trim() || undefined,
        terms: String(formData.get('terms') ?? '').trim() || undefined,
        items
      }

      if (props.quote()) {
        await quoteService.update(props.quote()!.id, { ...payload, status: status() as QuoteType['status'] })
        toast.addToast({ title: 'Cotización actualizada', type: 'success' })
      } else {
        await quoteService.create(payload)
        toast.addToast({ title: 'Cotización creada', type: 'success' })
      }

      navigate('/admin/cotizaciones')
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const pageTitle = createMemo(() => (props.quote() ? 'Editar Cotización' : 'Nueva Cotización'))

  return (
    <>
      <div class="pb-4">
        {/* Cabecera con la barra de acciones rápidas: crear cliente o servicio
            sin salir de la cotización, y atajos a sus pantallas completas. */}
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between mb-6">
          <div class="flex items-center gap-3 min-w-0">
            <GoBack label="Volver a cotizaciones" fallbackPath="/admin/cotizaciones" />
            <div class="min-w-0">
              <h1 class="text-xl font-bold text-foreground truncate">{pageTitle()}</h1>
              <p class="text-sm text-foreground-subtle">Elige servicios del catálogo o escribe uno libre</p>
            </div>
          </div>

          <div class="flex items-center gap-2 flex-wrap">
            <Button.Group variant="soft" color="default">
              <Menu
                trigger={<Button icon="material-symbols-light:more-vert" tooltip="Más acciones" />}
                items={[
                  { label: 'Nuevo cliente', icon: 'material-symbols-light:person-add-outline', onClick: () => setShowClientModal(true), color: 'primary' },
                  { label: 'Nuevo servicio', icon: 'material-symbols-light:add-business-outline', onClick: () => setShowServiceModal(true), color: 'primary' },
                  { label: 'Ver catálogo de servicios', icon: 'material-symbols-light:handyman-outline', onClick: () => navigate('/admin/servicios') },
                  { label: 'Administrar clientes', icon: 'material-symbols-light:group-outline', onClick: () => navigate('/admin/clientes') }
                ]}
              />
              <Button type="submit" form="quote-form" color="primary" disabled={isSubmitting()} prependIcon="material-symbols-light:save-outline">
                <Show
                  when={isSubmitting()}
                  fallback={
                    <Show when={props.quote()} fallback="Crear">
                      Guardar
                    </Show>
                  }
                >
                  Guardando...
                </Show>
              </Button>
            </Button.Group>
          </div>
        </div>

        <Show when={initialized()}>
          <Form id="quote-form" onSubmit={handleSubmit} initialValues={{ title: titleValue(), description: descriptionValue(), notes: notesValue(), terms: termsValue() }}>
            {/* Dos columnas: a la izquierda lo que se redacta, a la derecha lo
                que se consulta mientras tanto (totales y condiciones). */}
            <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
              <div class="xl:col-span-2 space-y-6">
                <Card>
                  <Card.Header>
                    <h2 class="font-semibold text-foreground">Datos de la propuesta</h2>
                  </Card.Header>
                  <Card.Body>
                    <div class="space-y-4">
                      <Input name="title" label="Título de la propuesta" placeholder="Plataforma web para..." required value={titleValue()} onInput={e => setTitleValue(e.currentTarget.value)} />

                      <div>
                        <Select
                          label="Cliente"
                          options={customerOptions()}
                          value={customerId()}
                          onChange={v => setCustomerId(String(v ?? ''))}
                          placeholder="Sin cliente asociado"
                          autocomplete
                          clearable
                          endIcon={
                            <Button
                              type="button"
                              variant="text"
                              color="primary"
                              size="sm"
                              class="px-2"
                              onClick={e => {
                                e.stopPropagation()
                                setShowClientModal(true)
                              }}
                              prependIcon="material-symbols-light:add"
                              aria-label="Nuevo cliente"
                              tooltip="Crear nuevo cliente"
                            >
                              Nuevo
                            </Button>
                          }
                        />
                        <Show when={selectedCustomer()}>
                          {customer => (
                            <p class="text-xs text-foreground-subtle mt-1.5 flex items-center gap-1">
                              <Icon icon="material-symbols-light:badge-outline" size={14} />
                              {[customer().document, customer().phone, customer().address].filter(Boolean).join(' · ') || 'Sin datos de contacto'}
                            </p>
                          )}
                        </Show>
                      </div>

                      <Textarea
                        name="description"
                        label="Descripción"
                        rows={2}
                        placeholder="Contexto y objetivo de la propuesta"
                        value={descriptionValue()}
                        onInput={e => setDescriptionValue(e.currentTarget.value)}
                      />
                    </div>
                  </Card.Body>
                </Card>

                <Show
                  when={isMobile()}
                  fallback={
                    <LineTable
                      lines={lines}
                      formatMoney={money}
                      lineLabel="Servicio"
                      title="Servicios cotizados"
                      subtitle={`${lines.length} línea(s)`}
                      addLabel="Añadir servicio"
                      onAdd={() => openLineModal()}
                      onEdit={openLineModal}
                      onDuplicate={duplicateLine}
                      onRemove={removeLine}
                    />
                  }
                >
                  <div class="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <h2 class="font-semibold text-foreground">Servicios cotizados</h2>
                      <p class="text-xs text-foreground-subtle mt-0.5">{lines.length} línea(s)</p>
                    </div>
                    <Button type="button" size="sm" variant="soft" color="default" onClick={() => openLineModal()} prependIcon="material-symbols-light:add">
                      Añadir servicio
                    </Button>
                  </div>
                  <Show
                    when={lines.length > 0}
                    fallback={
                      <div class="py-10 text-center">
                        <Icon icon="material-symbols-light:receipt-long-outline" size={40} class="mx-auto mb-2 text-foreground-subtle" />
                        <p class="text-sm font-medium text-foreground">Todavía no has añadido servicios</p>
                        <p class="text-xs text-foreground-subtle mt-1">Elige uno del catálogo o añade una línea libre.</p>
                      </div>
                    }
                  >
                    <div class="space-y-2">
                      <For each={lines}>
                        {(line, index) => <LineSwipeCard line={line} index={index()} formatMoney={money} onEdit={openLineModal} onDuplicate={duplicateLine} onRemove={removeLine} />}
                      </For>
                    </div>
                  </Show>
                </Show>

                <Card>
                  <Card.Header>
                    <h2 class="font-semibold text-foreground">Notas y condiciones</h2>
                  </Card.Header>
                  <Card.Body>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <Textarea name="notes" label="Notas" rows={4} placeholder="Aclaraciones para el cliente" value={notesValue()} onInput={e => setNotesValue(e.currentTarget.value)} />
                      <Textarea
                        name="terms"
                        label="Términos y condiciones"
                        rows={4}
                        placeholder="Forma de pago, tiempos de entrega..."
                        value={termsValue()}
                        onInput={e => setTermsValue(e.currentTarget.value)}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </div>

              {/* Resumen y condiciones: acompañan la redacción sin estorbar. */}
              <div class="space-y-6 xl:sticky xl:top-4">
                <Card color="primary" style="border">
                  <Card.Header>
                    <h2 class="font-semibold text-foreground">Resumen</h2>
                  </Card.Header>
                  <Card.Body>
                    <div class="space-y-2">
                      <div class="flex justify-between text-sm">
                        <span class="text-foreground-subtle">Subtotal</span>
                        <span class="text-foreground font-medium">{money(totals().subtotal)}</span>
                      </div>
                      <Show when={totals().discount > 0}>
                        <div class="flex justify-between text-sm">
                          <span class="text-foreground-subtle">Descuento{globalDiscountType() === 'percent' ? ` (${globalDiscount()}%)` : ''}</span>
                          <span class="text-foreground font-medium">- {money(totals().discount)}</span>
                        </div>
                      </Show>
                      <Show when={taxRate() > 0}>
                        <div class="flex justify-between text-sm">
                          <span class="text-foreground-subtle">Impuesto ({taxRate()}%)</span>
                          <span class="text-foreground font-medium">{money(totals().taxAmount)}</span>
                        </div>
                      </Show>
                      <div class="flex justify-between items-baseline pt-3 mt-1 border-t border-border">
                        <span class="font-semibold text-foreground">Total</span>
                        <span class="text-xl font-bold text-primary">{money(totals().total)}</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Card>
                  <Card.Header>
                    <h2 class="font-semibold text-foreground">Condiciones</h2>
                  </Card.Header>
                  <Card.Body>
                    <div class="space-y-4">
                      <Select label="Moneda" options={monedaOptions()} value={currency()} onChange={v => setCurrency(String(v ?? 'COP'))} />
                      <DateInput mode="date" label="Válida hasta" value={validUntil()} onChange={value => setValidUntil(value)} placeholder="Sin vencimiento" />
                      <Input label="Impuesto (%)" type="number" min="0" step="0.01" value={taxRate()} onInput={e => setTaxRate(Number(e.currentTarget.value) || 0)} />
                      <DiscountField
                        label="Descuento global"
                        value={globalDiscount()}
                        type={globalDiscountType()}
                        currency={currency()}
                        locale={monedaLocale()}
                        resolved={totals().discount}
                        formatMoney={money}
                        onValueChange={setGlobalDiscount}
                        onTypeChange={setGlobalDiscountType}
                      />
                      <Show when={props.quote()}>
                        <Select label="Estado" options={STATUS_OPTIONS} value={status()} onChange={v => setStatus(String(v ?? 'draft'))} />
                      </Show>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </Form>
        </Show>
      </div>

      <NewClientModal
        isOpen={showClientModal()}
        isSubmitting={isCreatingClient()}
        onClose={() => setShowClientModal(false)}
        onSubmit={handleCreateClient}
        subtitle="Se seleccionará en la cotización al crearlo"
      />

      <NewServiceModal
        isOpen={showServiceModal()}
        isSubmitting={isCreatingService()}
        currency={currency()}
        locale={monedaLocale()}
        onClose={() => setShowServiceModal(false)}
        onSubmit={handleCreateService}
        subtitle="Queda en tu catálogo y se añade a esta cotización"
      />

      <LineItemModal
        isOpen={showLineModal()}
        onClose={() => {
          setShowLineModal(false)
          setEditingLineIndex(null)
        }}
        onAdd={saveLine}
        initialLine={editingLineIndex() != null ? lines[editingLineIndex()!] : null}
        catalogOptions={catalogOptions()}
        services={catalog() ?? []}
        currency={currency()}
        locale={monedaLocale()}
        formatMoney={money}
        lineLabel="Servicio"
      />
      <alert.AlertComponent />
    </>
  )
}

export default QuoteForm
